<template>
  <div class="accordion accordion-flush mt-4" id="versionHistoryAccordion" v-if="data">
      <div class="accordion-item">
          <h2 class="accordion-header" id="flush-headingOne">
              <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#flush-collapseOne"
                  aria-expanded="false"
                  aria-controls="flush-collapseOne"
              >
                  <strong class="text-default">Version History</strong>
              </button>
          </h2>
          <div
              id="flush-collapseOne"
              class="accordion-collapse collapse"
              aria-labelledby="flush-headingOne"
              data-bs-parent="#versionHistoryAccordion"
          >
              <div class="accordion-body p-0">
                  <div v-if="loading" class="text-center p-3">
                      <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                      </div>
                  </div>
                  <p v-else-if="!versions || versions.length === 0" class="text-center p-3 text-muted">
                      No version history available.
                  </p>
                  <div v-else class="table-responsive">
                      <table class="table table-sm table-hover mb-0">
                          <thead>
                              <tr>
                                  <th scope="col">Date Superseded</th>
                                  <th scope="col" class="text-center">Version</th>
                                  <th scope="col" class="text-center">Updated By</th>
                                  <th scope="col" class="text-center">Action</th>
                              </tr>
                          </thead>
                          <tbody>
                              <tr v-for="version in versions" :key="version.ID">
                                <td class="align-middle">{{ formatDisplayDate(version.UpdatedAt) }}</td>
                                <td class="text-center align-middle">{{ formatVersion(version.version) }}</td>
                                <td class="text-center align-middle">
                                  <span v-if="version.updated_by_name && version.updated_by_name !== 'Unknown User' && version.updated_by_name !== 'N/A'"
                                        class="initials-badge"
                                        :style="{ backgroundColor: getBadgeColor(version.updated_by_name) }"
                                        v-tooltip.top="version.updated_by_name">
                                    {{ getInitials(version.updated_by_name) }}
                                  </span>
                                  <span v-else-if="version.updated_by_name === 'Unknown User'">Deleted User</span>
                                  <span v-else>N/A</span>
                                </td>
                                  <td class="text-center">
                                      <button
                                          @click="onPreviewVersion(version)"
                                          class="btn btn-outline-secondary btn-sm"
                                          title="Preview Version"
                                      >
                                          <i class="bi bi-eye"></i> View
                                      </button>
                                  </td>
                              </tr>
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      </div>
  </div>
</template>

<script setup>
import { ref, defineProps, watch, getCurrentInstance } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';

const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});

const { proxy } = getCurrentInstance();


const router = useRouter();
const versions = ref([]);
const loading = ref(true);
const hasFetched = ref(false);
const store =  proxy.$store;


watch(() => props.data, (newData) => {
  if (newData && newData.ID && !hasFetched.value) {
    fetchVersions();
  }
}, { immediate: true });

async function fetchVersions() {
  if (!props.data || !props.data.ID || hasFetched.value) {
    return;
  }

  loading.value = true;
  try {
    const orgId = props.data.organisation_id;
    const policyId = props.data.ID;

    const response = await axios.get(`/orgadmin/trainings/organisations/${orgId}/training-policy/${policyId}/history`);
    if (response.data.success) {
      const fetchedVersions = response.data.versions || [];
      const userIds = [...new Set(fetchedVersions.map(v => v.updated_by).filter(Boolean))];

      if (userIds.length > 0) {
        try {
          const usersResponse = await axios.post('/users/'+store.state.auth.jwtPayload.user_id+'/basic-info', { user_ids: userIds });
          if (usersResponse.data.success) {
            const userMap = usersResponse.data.users.reduce((map, user) => {
              map[user.id] = `${user.first_name} ${user.last_name}`;
              return map;
            }, {});

            fetchedVersions.forEach(version => {
              if (version.updated_by) {
                version.updated_by_name = userMap[version.updated_by] || 'Unknown User';
              } else {
                version.updated_by_name = 'N/A';
              }
            });
          }
        } catch (userError) {
          console.error('Failed to fetch bulk user info:', userError);
        }
      }

      versions.value = fetchedVersions;
    } else {
      console.error('Failed to fetch versions:', response.data.message);
    }
  } catch (error) {
    console.error('Error fetching policy versions:', error);
  } finally {
    loading.value = false;
    hasFetched.value = true;
  }
}

function onPreviewVersion(version) {
  if (!props.data || !props.data.ID || !version || !version.ID) {
    console.error("Missing data for navigation.");
    return;
  }

  const routeData = router.resolve({
    name: 'PolicyPreview',
    params: {id: version.ID}
  });
  window.open(routeData.href, '_blank');
}

/**
 * Generates initials from a full name.
 * @param {string} name - The user's full name.
 * @returns {string} The initials.
 */
function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Generates a consistent color for the badge from a predefined list.
 * @param {string} name - The user's full name.
 * @returns {string} A hex color code.
 */
function getBadgeColor(name) {
  const colors = [
    '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e',
    '#16a085', '#27ae60', '#2980b9', '#8e44ad', '#f39c12',
    '#d35400', '#c0392b', '#7f8c8d'
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash % colors.length)];
}

/**
 * Formats a version number to a whole number.
 * @param {number | string} version - The version number.
 * @returns {string} The formatted version string.
 */
function formatVersion(version) {
  const num = parseFloat(version);
  return !isNaN(num) ? num.toFixed(0) : String(version);
}

/**
 * Formats an ISO date string.
 */
function formatDisplayDate(dateString) {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, '-');
    } catch (error) {
        return dateString;
    }
}
</script>

<style scoped>
/* Use Bootstrap utility classes and variables for styling */
.accordion-button {
    color: var(--bs-dark); /* Or your specific brand color */
    font-size: 1rem;
}

.accordion-button:not(.collapsed) {
    background-color: #f8f9fa; /* A light grey for the active header */
}

/* Remove default padding from accordion body to make table flush */
.accordion-body.p-0 {
    padding: 0 !important;
}

/* Ensure table headers are styled correctly */
.table thead th {
    font-size: 0.85rem;
    font-weight: 500;
    color: #6c757d; /* Muted text color for headers */
    background-color: #f8f9fa;
    border-top: none;
    border-bottom: 2px solid #dee2e6;
    padding: 0.5rem 0.75rem;
}

.table tbody td {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
}

.table-hover tbody tr:hover {
    background-color: #f1f1f1;
}

.btn-sm {
    /* Make sure icons and text align */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0.5rem;
}

.initials-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  cursor: default;
}
</style>